/* ==========================================================================
   LIVVY — BUY BOX
   --------------------------------------------------------------------------
   Three self-contained custom elements. No dependency on the Stretch theme's
   own JS, but they cooperate with it:

   - the add-to-cart form stays a real `{% form 'product' %}` inside the
     theme's <product-form>, so the AJAX add + cart drawer keep working;
   - we only ever write to the form's hidden `id` and `selling_plan` inputs.

   <lv-buy-box>   option pills, purchase type, prices, gallery sync
   <lv-gallery>   main image + thumbnails, swipe carousel on mobile
   <lv-accordion> FAQ rows: smooth height animation, one open at a time
   ========================================================================== */

(() => {
  const money = (cents, format) => {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Match Liquid's money_without_trailing_zeros: a whole amount shows as $50,
    // not $50.00. The Liquid-rendered price already does this, so the JS has to
    // agree or the price would gain ".00" the moment a variant changes.
    const dot = decimals === '00' ? '' : `.${decimals}`;
    const comma = decimals === '00' ? '' : `,${decimals}`;

    // Shopify money formats we care about; anything else falls back to the
    // amount with the currency symbol taken from the format string.
    if (!format) return `$${withCommas}${dot}`;

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, `${withCommas}${dot}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${withCommas}${comma}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas);
  };

  /* ======================================================================
     BUY BOX
     ====================================================================== */

  class LvBuyBox extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-lv-variants]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }

      this.variants = this.data.variants || [];
      this.moneyFormat = this.data.moneyFormat;
      this.unitSingular = this.data.unitSingular || 'can';
      this.unitPlural = this.data.unitPlural || 'Cans';

      this.form = this.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.planInput = this.form ? this.form.querySelector('input[name="selling_plan"]') : null;

      this.addEventListener('change', this.onChange.bind(this));

      this.purchaseType = this.dataset.purchaseType || 'subscription';
      this.useVariantGallery = this.dataset.useVariantGallery === 'true';
      this.gallerySignature = this.gallerySignatureFor(this.currentVariant);
      this.render();
    }

    gallerySignatureFor(variant) {
      if (!this.useVariantGallery) return '';
      const images = (variant && variant.thumbnailGallery) || [];
      if (!images.length) return 'fallback';
      return images.map((image) => image.src).join('|');
    }

    /* Which option values are currently picked, by option position */
    get selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-lv-option-input]:checked').forEach((input) => {
        values[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
      });
      return values;
    }

    get currentVariant() {
      const selected = this.selectedOptions;
      return (
        this.variants.find((variant) =>
          variant.options.every((value, index) => value === selected[index])
        ) || null
      );
    }

    onChange(event) {
      const target = event.target;
      if (!target) return;

      if (target.matches('[data-lv-purchase-type]')) {
        this.purchaseType = target.value;
        this.render();
        return;
      }

      if (target.matches('[data-lv-frequency]')) {
        // Remember the choice by plan name, not id: ids differ per variant, so
        // switching pack size would otherwise reset the frequency.
        const opt = target.selectedOptions[0];
        this.frequencyName = opt ? opt.dataset.planName : null;
        this.render();
        return;
      }

      if (target.matches('[data-lv-option-input]')) {
        this.render();
      }
    }

    /* The plan that should be used for the selected variant: the one whose name
       matches the visitor's last choice, else the section's configured plan,
       else the first. */
    planFor(variant) {
      const plans = (variant && variant.sellingPlans) || [];
      if (!plans.length) return null;

      if (this.frequencyName) {
        const kept = plans.find((pl) => pl.name === this.frequencyName);
        if (kept) return kept;
      }

      const configured = plans.find((pl) => pl.id === variant.sellingPlanId);
      return configured || plans[0];
    }

    syncFrequency(variant) {
      const wrap = this.querySelector('[data-lv-frequency-wrap]');
      const select = this.querySelector('[data-lv-frequency]');
      if (!wrap || !select) return;

      const plans = (variant && variant.sellingPlans) || [];
      // Nothing to choose between - keep the static line instead.
      if (plans.length < 2) {
        wrap.hidden = true;
        return;
      }

      const active = this.planFor(variant);
      const signature = plans.map((pl) => pl.id).join(',');

      if (select.dataset.signature !== signature) {
        select.innerHTML = '';
        plans.forEach((pl) => {
          const option = document.createElement('option');
          option.value = pl.id;
          option.textContent = pl.name;
          option.dataset.planName = pl.name;
          select.appendChild(option);
        });
        select.dataset.signature = signature;
      }

      if (active) select.value = String(active.id);
      wrap.hidden = false;
    }

    render() {
      const variant = this.currentVariant;

      this.syncOptionAvailability();
      this.syncPurchaseType(variant);
      this.syncSaveBadges();
      this.syncFrequency(variant);
      this.syncPrices(variant);
      this.syncForm(variant);
      this.syncGallery(variant);

      this.dispatchEvent(
        new CustomEvent('lv-buy-box:change', { bubbles: true, detail: { variant } })
      );
    }

    /* Grey out option values that cannot be combined with the current pick */
    syncOptionAvailability() {
      const selected = this.selectedOptions;

      this.querySelectorAll('[data-lv-option-input]').forEach((input) => {
        const position = parseInt(input.dataset.optionPosition, 10) - 1;
        const candidate = [...selected];
        candidate[position] = input.value;

        const match = this.variants.find((variant) =>
          variant.options.every((value, index) =>
            candidate[index] === undefined ? true : value === candidate[index]
          )
        );

        const available = Boolean(match && match.available);
        input.closest('[data-lv-option]')?.classList.toggle('is-unavailable', !available);
      });
    }

    /* A product with no selling plan on the current variant only gets the
       one-time card, and it is forced on. */
    syncPurchaseType(variant) {
      const hasPlan = Boolean(variant && variant.sellingPlanId);
      this.classList.toggle('lv-bb--no-plan', !hasPlan);

      if (!hasPlan && this.purchaseType === 'subscription') {
        this.purchaseType = 'one_time';
      }

      this.querySelectorAll('[data-lv-purchase-type]').forEach((input) => {
        const checked = input.value === this.purchaseType;
        if (input.checked !== checked) input.checked = checked;
        input.closest('[data-lv-purchase-card]')?.classList.toggle('is-selected', checked);
      });

      this.setAttribute('data-purchase-type', this.purchaseType);
    }

    /* The pack-size badges carry both figures - the subscription saving and the
       one-time saving - and show whichever matches the selected purchase type.
       They used to be fixed at render time, so switching to One-Time left them
       advertising subscription discounts while the panel below showed one-time
       prices. A pack with no saving on the current type hides its badge rather
       than showing "Save 0%". */
    syncSaveBadges() {
      const key = this.purchaseType === 'subscription' ? 'saveSubscription' : 'saveOneTime';

      this.querySelectorAll('[data-lv-option-save]').forEach((badge) => {
        const percent = parseInt(badge.dataset[key], 10) || 0;
        badge.hidden = percent <= 0;
        if (percent > 0) {
          badge.textContent = `${badge.dataset.savePrefix || 'Save'} ${percent}%`;
        }
      });
    }

    syncPrices(variant) {
      if (!variant) return;

      // The chosen frequency, not whichever plan happened to be resolved first.
      const plan = this.planFor(variant);
      const planPrice = plan ? plan.price : variant.sellingPlanPrice;
      const planPercent = plan ? plan.percent : variant.sellingPlanPercent;

      const subscribed = this.purchaseType === 'subscription' && variant.sellingPlanId;
      const price = subscribed ? planPrice : variant.price;
      const compareAt = variant.compareAtPrice > price ? variant.compareAtPrice : 0;
      const units = variant.unitCount || 1;

      this.setText('[data-lv-price]', money(price, this.moneyFormat));
      this.setText('[data-lv-compare-price]', compareAt ? money(compareAt, this.moneyFormat) : '');
      this.toggle('[data-lv-compare-price]', Boolean(compareAt));

      // Per-card prices, always both rendered so switching is instant
      this.setText('[data-lv-sub-price]', money(planPrice, this.moneyFormat));
      this.setText('[data-lv-onetime-price]', money(variant.price, this.moneyFormat));

      const subCompare = variant.compareAtPrice > planPrice ? variant.compareAtPrice : 0;
      this.setText('[data-lv-sub-compare]', subCompare ? money(subCompare, this.moneyFormat) : '');
      this.toggle('[data-lv-sub-compare]', Boolean(subCompare));

      const onetimeCompare = variant.compareAtPrice > variant.price ? variant.compareAtPrice : 0;
      this.setText('[data-lv-onetime-compare]', onetimeCompare ? money(onetimeCompare, this.moneyFormat) : '');
      this.toggle('[data-lv-onetime-compare]', Boolean(onetimeCompare));

      this.setText(
        '[data-lv-sub-unit-price]',
        `${money(Math.round(planPrice / units), this.moneyFormat)}/${this.unitSingular}`
      );
      this.setText(
        '[data-lv-onetime-unit-price]',
        `${money(Math.round(variant.price / units), this.moneyFormat)}/${this.unitSingular}`
      );

      const unitLabel = `${units} ${units === 1 ? this.unitSingular : this.unitPlural}`;
      this.setText('[data-lv-unit-count]', unitLabel);
      // "12 cans every 4 weeks". When the picker is showing, the frequency half
      // comes from the chosen plan so the two can never disagree; the plan name
      // is used as-is ("Every 4 weeks" -> "every 4 weeks").
      const hasChoice = ((variant.sellingPlans || []).length > 1);
      const planPhrase = hasChoice && plan ? plan.name.charAt(0).toLowerCase() + plan.name.slice(1) : null;
      this.querySelectorAll('[data-lv-sub-frequency]').forEach((el) => {
        const tail = planPhrase || el.dataset.lvSubFrequency;
        el.textContent = `${unitLabel} ${tail}`.trim();
      });

      // Headline "BEST VALUE — SAVE x%" bar
      const bestPct = planPercent || variant.discountPercent || 0;
      this.setText('[data-lv-best-value-percent]', `${bestPct}%`);
      this.toggle('[data-lv-best-value]', bestPct > 0);
    }

    syncForm(variant) {
      if (this.idInput && variant) this.idInput.value = variant.id;

      if (this.planInput) {
        const plan = this.planFor(variant);
        const subscribed = this.purchaseType === 'subscription' && plan;
        this.planInput.value = subscribed ? plan.id : '';
        this.planInput.disabled = !subscribed;
      }

      const button = this.querySelector('[data-lv-submit]');
      if (button) {
        const soldOut = !variant || !variant.available;
        button.disabled = soldOut;
        button.textContent = soldOut
          ? button.dataset.soldOutText || 'Sold out'
          : button.dataset.defaultText || 'Add to cart';
      }
    }

    syncGallery(variant) {
      const gallery = this.querySelector('lv-gallery');
      if (!gallery) return;

      if (this.useVariantGallery) {
        const signature = this.gallerySignatureFor(variant);
        if (this.gallerySignature === signature) return;
        this.gallerySignature = signature;

        const images = (variant && variant.thumbnailGallery) || [];
        if (images.length) {
          if (typeof gallery.setImages === 'function') gallery.setImages(images);
        } else if (typeof gallery.restoreFallback === 'function') {
          gallery.restoreFallback();
        }
        return;
      }

      if (!variant || !variant.featuredMediaId) return;
      if (typeof gallery.selectMedia === 'function') {
        gallery.selectMedia(variant.featuredMediaId);
      }
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = value;
      });
    }

    toggle(selector, show) {
      this.querySelectorAll(selector).forEach((el) => {
        el.hidden = !show;
      });
    }
  }

  /* ======================================================================
     GALLERY
     ====================================================================== */

  class LvGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-lv-gallery-viewport]');
      this.thumbsWrap = this.querySelector('.lv-gallery__thumbs');

      this.thumbsWrap?.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-lv-gallery-thumb]');
        if (!thumb || !this.thumbsWrap.contains(thumb)) return;
        event.preventDefault();
        const index = this.thumbs.indexOf(thumb);
        if (index > -1) this.goTo(index);
      });

      this.querySelector('[data-lv-gallery-prev]')?.addEventListener('click', () =>
        this.step(-1)
      );
      this.querySelector('[data-lv-gallery-next]')?.addEventListener('click', () =>
        this.step(1)
      );

      this.frame = this.querySelector('.lv-gallery__frame');
      this.refresh();
    }

    /* The single funnel every slide-set change goes through - first render,
       a variant swap in setImages(), and restoreFallback(). Swiper is rebuilt
       here rather than patched, because those two replace the track's innerHTML
       wholesale and would otherwise leave Swiper describing slides that no
       longer exist. */
    refresh() {
      this.slides = Array.from(this.querySelectorAll('[data-lv-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-lv-gallery-thumb]'));
      this.buildSwiper();
      this.setActive(0);
    }

    buildSwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (!this.frame || this.slides.length < 2) return;

      if (!window.Swiper) {
        // Deferred script may not have run yet; rebuild once it has.
        if (!this.awaitingSwiper) {
          this.awaitingSwiper = true;
          window.addEventListener('load', () => {
            this.awaitingSwiper = false;
            this.buildSwiper();
            this.setActive(0);
          }, { once: true });
        }
        return;
      }

      this.swiper = new window.Swiper(this.frame, {
        slidesPerView: 1,
        speed: 400,
        // Wraps in both directions: the arrow on the last image goes to the
        // first, and so does a swipe, which is what it always implied.
        loop: true,
        a11y: { enabled: true },
        // Horizontal trackpad swipes (two-finger on a Mac) move the slides.
        // forceToAxis keeps vertical scrolling over the image as page scroll.
        mousewheel: { forceToAxis: true },
        on: {
          slideChange: (swiper) => this.setActive(swiper.realIndex),
        },
      });
    }

    escapeAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
    }

    setImages(images) {
      if (!this.viewport) return;

      const list = Array.isArray(images) ? images : [];
      this.viewport.innerHTML = list
        .map((image) => {
          const alt = this.escapeAttr(image.alt || '');
          const src = this.escapeAttr(image.src || '');
          const srcset = this.escapeAttr(image.srcset || '');
          const width = image.width || '';
          const height = image.height || '';
          return `<div class="swiper-slide lv-gallery__slide" data-lv-gallery-slide><img src="${src}" srcset="${srcset}" sizes="(max-width: 999px) 100vw, 50vw" width="${width}" height="${height}" alt="${alt}" loading="lazy"></div>`;
        })
        .join('');

      if (this.thumbsWrap) {
        this.thumbsWrap.innerHTML = list
          .map((image) => {
            const src = this.escapeAttr(image.thumb || image.src || '');
            const srcset = this.escapeAttr(image.thumbSrcset || '');
            return `<button type="button" class="lv-gallery__thumb" data-lv-gallery-thumb><img src="${src}" srcset="${srcset}" sizes="200px" alt="" loading="lazy"></button>`;
          })
          .join('');
      }

      this.refresh();
      this.goTo(0, true);
    }

    restoreFallback() {
      const template = this.querySelector('[data-lv-gallery-fallback]');
      if (!template) return;

      const slides = template.content.querySelector('[data-lv-gallery-fallback-slides]');
      const thumbs = template.content.querySelector('[data-lv-gallery-fallback-thumbs]');
      if (this.viewport && slides) this.viewport.innerHTML = slides.innerHTML;
      if (this.thumbsWrap && thumbs) this.thumbsWrap.innerHTML = thumbs.innerHTML;

      this.refresh();
      this.goTo(0, true);
    }

    get index() {
      return this.swiper ? this.swiper.realIndex : 0;
    }

    step(direction) {
      if (!this.swiper) return;
      if (direction > 0) this.swiper.slideNext();
      else this.swiper.slidePrev();
    }

    goTo(index, instant) {
      if (!this.swiper) return;
      // slideToLoop takes the real index, so thumbs keep their 1:1 mapping
      // however far the loop has rotated the slides.
      this.swiper.slideToLoop(index, instant ? 0 : 400);
    }

    selectMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }

    setActive(index) {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => {
        const current = i === index;
        thumb.classList.toggle('is-active', current);
        // `is-current` is what the CSS hides — the strip keeps its 1:1 index
        // mapping with the slides because the thumb is hidden, not removed.
        thumb.classList.toggle('is-current', current);
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
        thumb.setAttribute('aria-hidden', current ? 'true' : 'false');
        thumb.tabIndex = current ? -1 : 0;
      });

      // Nothing is ever a dead end while the gallery loops. Without this the
      // next arrow greyed out on the last image - the same arrow that is
      // supposed to take you round to the first.
      const looping = Boolean(this.swiper);
      const prev = this.querySelector('[data-lv-gallery-prev]');
      const next = this.querySelector('[data-lv-gallery-next]');
      if (prev) prev.disabled = looping ? false : index === 0;
      if (next) next.disabled = looping ? false : index === this.slides.length - 1;
    }
  }

  /* ======================================================================
     ACCORDION — smooth open/close, single open per group
     ====================================================================== */

  class LvAccordion extends HTMLElement {
    connectedCallback() {
      this.summary = this.querySelector('[data-lv-accordion-summary]');
      this.panel = this.querySelector('[data-lv-accordion-panel]');
      if (!this.summary || !this.panel) return;

      this.open = this.hasAttribute('open');
      this.panel.style.height = this.open ? 'auto' : '0px';
      this.summary.setAttribute('aria-expanded', this.open ? 'true' : 'false');

      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });
    }

    toggle() {
      if (this.open) {
        this.close();
        return;
      }

      // Only one open at a time within the same group
      const group = this.closest('[data-lv-accordion-group]');
      if (group) {
        group.querySelectorAll('lv-accordion').forEach((item) => {
          if (item !== this && item.open) item.close();
        });
      }

      this.show();
    }

    show() {
      this.open = true;
      this.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');
      this.animateTo(this.panel.scrollHeight, 'auto');
    }

    close() {
      this.open = false;
      this.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false');
      this.panel.style.height = `${this.panel.scrollHeight}px`;
      // Force a reflow so the browser animates from the measured height
      void this.panel.offsetHeight;
      this.animateTo(0, '0px');
    }

    animateTo(target, settled) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.style.height = settled;
        return;
      }

      this.panel.style.height = `${target}px`;

      const onEnd = (event) => {
        if (event.propertyName !== 'height') return;
        this.panel.removeEventListener('transitionend', onEnd);
        if (this.open) this.panel.style.height = settled;
      };

      this.panel.addEventListener('transitionend', onEnd);
    }
  }

  if (!customElements.get('lv-buy-box')) customElements.define('lv-buy-box', LvBuyBox);
  if (!customElements.get('lv-gallery')) customElements.define('lv-gallery', LvGallery);
  if (!customElements.get('lv-accordion')) customElements.define('lv-accordion', LvAccordion);
})();
